import React from 'react';

import { Text } from '@adsk/alloy-react-box';
import { displayDate, VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import { useOnlyLocale } from '@adsk/alloy-react-locale';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { useTranslation } from '../../i18n';
import { getDiffFromToday } from '../../utils/date';

interface DateRelativeProps {
  date: Date;
  toolTipContent: boolean;
}

const DateRelativeText: React.FC<DateRelativeProps> = ({
  date,
  toolTipContent = false,
}) => {
  const { t } = useTranslation();

  const dayDiff = getDiffFromToday(date);

  let subText = null;
  if (dayDiff > 0 && dayDiff <= 3) {
    subText =
      dayDiff === 1 ? t('DUE_TOMORROW') : t('DUE_IN_X_DAYS', { days: dayDiff });
  } else if (dayDiff < 0) {
    subText =
      dayDiff === -1
        ? t('OVERDUE_YESTERDAY')
        : t('OVERDUE_X_DAYS', { days: Math.abs(dayDiff) });
  } else if (dayDiff === 0) {
    subText = t('DUE_TODAY');
  }

  if (toolTipContent) {
    return <span>{subText}</span>;
  }
  return <Text color={dayDiff < 0 ? 'red700' : 'adskBlue500'}>{subText}</Text>;
};

const DateToolTipContent: React.FC<DateProps> = ({ date, relative }) => {
  const { locale } = useOnlyLocale();
  if (!date) {
    return <span>-</span>;
  }
  const dateToDisplay = displayDate(date, {
    format: VALID_DATE_FORMATS.DEFAULT,
    locale,
  });

  return (
    <div>
      {dateToDisplay}
      {relative && (
        <>
          <br />
          <DateRelativeText date={date} toolTipContent={true} />
        </>
      )}
    </div>
  );
};

interface DateProps {
  date?: Date;
  relative?: boolean;
}

const DateCell: React.FC<DateProps> = ({ date, relative = false }) => {
  const { locale } = useOnlyLocale();

  if (!date) {
    return <span>-</span>;
  }

  const dateToDisplay = displayDate(date, {
    format: VALID_DATE_FORMATS.DEFAULT,
    locale,
  });

  return (
    <OverflowTooltip
      content={<DateToolTipContent date={date} relative={relative} />}
      autoTipOnEllipsis
    >
      <div>
        {dateToDisplay}
        {relative && (
          <>
            <br />
            <DateRelativeText date={date} toolTipContent={false} />
          </>
        )}
      </div>
    </OverflowTooltip>
  );
};

DateCell.displayName = 'SubmittalsDateDisplay';

export default DateCell;
